import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@promptwatch/ui/components/button";
import { DialogContent, DialogFooter } from "@promptwatch/ui/components/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@promptwatch/ui/components/form";
import { Input } from "@promptwatch/ui/components/input";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Image from "next/image";
import { usePostHog } from "posthog-js/react";
import { CalendarIcon, ArrowRightIcon, Mail, CheckCircle2 } from "lucide-react";
import { MonitoringChart } from "../sections/features-bento";
import { useState } from "react";
import { trpc } from "@/utils/trpc";
import { popModal } from ".";

const SignupSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  email: z.string().email({ message: "Invalid email address" }),
});

const VerificationSchema = z.object({
  code: z.string().min(1, { message: "Verification code is required" }),
});

type SignupValues = z.infer<typeof SignupSchema>;
type VerificationValues = z.infer<typeof VerificationSchema>;

const SignupModal = ({ type, onSuccess }: { type: "book-demo" | "get-started"; onSuccess?: () => void }) => {
  const posthog = usePostHog();
  const [step, setStep] = useState<"signup" | "verify" | "success">("signup");
  const [email, setEmail] = useState("");

  const signupMutation = trpc.freeToolUsers.signup.useMutation({
    onSuccess: () => {
      toast.success("Verification code sent to your email!");
      setStep("verify");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to sign up. Please try again.");
    },
  });

  const verifyMutation = trpc.freeToolUsers.verifyEmail.useMutation({
    onSuccess: (data) => {
      if (data.token) {
        // Store the token in localStorage
        localStorage.setItem("free-tools-token", data.token);
        toast.success("Email verified successfully!");
        setStep("success");

        posthog?.capture("signup_completed", {
          type,
        });

        // Handle success based on type
        if (type === "book-demo") {
          setTimeout(() => {
            window.open("https://cal.com/gijs-de-groot-ufxgwx/promptwatch15min", "_blank");
            onSuccess?.();
          }, 2000);
        } else {
          setTimeout(() => {
            onSuccess?.();
            popModal();
          }, 1500);
        }
      }
    },
    onError: (error) => {
      toast.error(error.message || "Invalid verification code. Please try again.");
    },
  });

  const signupForm = useForm<SignupValues>({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const verificationForm = useForm<VerificationValues>({
    resolver: zodResolver(VerificationSchema),
    defaultValues: {
      code: "",
    },
  });

  const onSignupSubmit = async (values: SignupValues) => {
    setEmail(values.email);

    posthog?.capture("signup_started", {
      type,
    });

    signupMutation.mutate(values);
  };

  const onVerificationSubmit = async (values: VerificationValues) => {
    verifyMutation.mutate({
      email,
      code: values.code,
    });
  };

  const handleResendCode = () => {
    const values = signupForm.getValues();
    signupMutation.mutate(values);
    toast.info("Verification code resent!");
  };

  const handleBookDemo = () => {
    window.open("https://cal.com/gijs-de-groot-ufxgwx/promptwatch15min", "_blank");
  };

  return (
    <DialogContent
      size="4xl"
      className="p-0 grid grid-cols-1 md:grid-cols-2 max-w-4xl overflow-hidden"
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <div className="flex flex-col justify-between h-full">
        <div className="flex flex-col">
          <div className="pl-5 pt-4 pb-3">
            {step === "signup" &&
              (type === "book-demo" ? (
                <>
                  <h2 className="text-xl font-semibold">Book a demo</h2>
                  <p className="text-muted-foreground text-sm">Let us show you how to boost your brand visibility in LLMs</p>
                </>
              ) : (
                <>
                  <h2 className="text-xl font-semibold">Create an account</h2>
                  <p className="text-muted-foreground text-sm">Start monitoring your AI visibility in real-time</p>
                </>
              ))}
            {step === "verify" && (
              <>
                <h2 className="text-xl font-semibold">Verify Your Email</h2>
                <p className="text-muted-foreground text-sm">We've sent a verification code to {email}</p>
              </>
            )}
          </div>

          {step === "signup" && (
            <Form {...signupForm}>
              <form onSubmit={signupForm.handleSubmit(onSignupSubmit)} className="space-y-4 p-5">
                <div className="flex gap-4">
                  <FormField
                    control={signupForm.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Eric" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={signupForm.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Schmidt" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={signupForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="eric@abc.xyz" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          )}

          {step === "verify" && (
            <div className="p-5">
              <div className="flex justify-center py-4">
                <div className="rounded-full bg-blue-100 p-3">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <Form {...verificationForm}>
                <form onSubmit={verificationForm.handleSubmit(onVerificationSubmit)} className="space-y-4">
                  <FormField
                    control={verificationForm.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Verification Code</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter 6-digit code"
                            {...field}
                            maxLength={6}
                            className="text-center text-lg tracking-widest"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="text-center">
                    <Button type="button" variant="link" onClick={handleResendCode} disabled={signupMutation.isPending} className="text-sm">
                      Didn't receive the code? Resend
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          )}

          {step === "success" && (
            <div className="p-5 mt-10 flex flex-col items-center text-center space-y-4">
              <div className="rounded-full bg-green-100 p-3 w-16 h-16 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold">Welcome to Promptwatch!</h3>
              <p className="text-muted-foreground">
                {type === "book-demo"
                  ? "Your account is ready! Redirecting you to book a demo..."
                  : "Your account has been created successfully. You're all set to start monitoring your AI visibility!"}
              </p>
              {type === "book-demo" && (
                <Button onClick={handleBookDemo} variant="default" className="mt-4 flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Book a Demo <ArrowRightIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>

        {(step === "signup" || step === "verify") && (
          <DialogFooter className="flex flex-row-reverse w-full justify-end p-5 mt-auto">
            {step === "signup" && (
              <Button onClick={signupForm.handleSubmit(onSignupSubmit)} className="w-full" disabled={signupMutation.isPending}>
                {signupMutation.isPending ? "Creating account..." : type === "book-demo" ? "Continue to Book Demo" : "Get Started"}
              </Button>
            )}
            {step === "verify" && (
              <Button onClick={verificationForm.handleSubmit(onVerificationSubmit)} className="w-full" disabled={verifyMutation.isPending}>
                {verifyMutation.isPending ? "Verifying..." : "Verify Email"}
              </Button>
            )}
          </DialogFooter>
        )}
      </div>

      <div className="relative bg-gray-50 border-l z-0 hidden md:block">
        <div className="absolute z-1 px-5 md:pt-6 max-w-sm">
          <Image className="h-7" src="/logo.svg" alt="Promptwatch logo" height={90} width={170} priority />
          <p className="my-4 text-2xl font-semibold">
            Increase your brand visibility in <span className="text-muted-foreground">AI search engines</span>
          </p>
        </div>
        <MonitoringChart showTooltip={false} height={430} />
      </div>
    </DialogContent>
  );
};

export { SignupModal };
